interface LoadingSkeletonProps {
  type?: "card" | "table" | "list" | "text";
  count?: number;
}

export function LoadingSkeleton({ type = "card", count = 1 }: LoadingSkeletonProps) {
  const skeletons = Array.from({ length: count }, (_, i) => i);

  if (type === "card") {
    return (
      <div className="skeleton-container">
        {skeletons.map((i) => (
          <div key={i} className="skeleton-card">
            <div className="skeleton-line skeleton-title"></div>
            <div className="skeleton-line skeleton-text"></div>
            <div className="skeleton-line skeleton-text-short"></div>
          </div>
        ))}
        <style>{`
          .skeleton-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1rem;
          }

          .skeleton-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 1.5rem;
            animation: pulse 1.5s ease-in-out infinite;
          }

          .skeleton-line {
            height: 12px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 4px;
            margin-bottom: 0.75rem;
          }

          .skeleton-title {
            height: 20px;
            width: 70%;
            margin-bottom: 1rem;
          }

          .skeleton-text {
            width: 100%;
          }

          .skeleton-text-short {
            width: 50%;
          }

          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
          }
        `}</style>
      </div>
    );
  }

  if (type === "table") {
    return (
      <div className="skeleton-table">
        <div className="skeleton-table-header">
          <div className="skeleton-line" style={{ width: "30%" }}></div>
          <div className="skeleton-line" style={{ width: "20%" }}></div>
          <div className="skeleton-line" style={{ width: "25%" }}></div>
          <div className="skeleton-line" style={{ width: "15%" }}></div>
        </div>
        {skeletons.map((i) => (
          <div key={i} className="skeleton-table-row">
            <div className="skeleton-line" style={{ width: "35%" }}></div>
            <div className="skeleton-line" style={{ width: "15%" }}></div>
            <div className="skeleton-line" style={{ width: "20%" }}></div>
            <div className="skeleton-line" style={{ width: "20%" }}></div>
          </div>
        ))}
        <style>{`
          .skeleton-table {
            width: 100%;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
          }

          .skeleton-table-header,
          .skeleton-table-row {
            display: flex;
            gap: 1rem;
            padding: 1rem;
            border-bottom: 1px solid #e5e7eb;
          }

          .skeleton-table-header {
            background: #f9fafb;
          }

          .skeleton-table-row:last-child {
            border-bottom: none;
          }

          .skeleton-line {
            height: 12px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 4px;
          }

          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
        `}</style>
      </div>
    );
  }

  if (type === "list") {
    return (
      <div className="skeleton-list">
        {skeletons.map((i) => (
          <div key={i} className="skeleton-list-item">
            <div className="skeleton-line" style={{ width: "80%" }}></div>
            <div className="skeleton-line" style={{ width: "60%" }}></div>
          </div>
        ))}
        <style>{`
          .skeleton-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .skeleton-list-item {
            padding: 1rem;
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
          }

          .skeleton-line {
            height: 12px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 4px;
            margin-bottom: 0.5rem;
          }

          .skeleton-line:last-child {
            margin-bottom: 0;
          }

          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
        `}</style>
      </div>
    );
  }

  // Default: text skeleton
  return (
    <div className="skeleton-text-container">
      {skeletons.map((i) => (
        <div key={i} className="skeleton-line"></div>
      ))}
      <style>{`
        .skeleton-text-container {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .skeleton-line {
          height: 16px;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
          width: 100%;
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
}
